/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
/* eslint-disable ember/no-runloop */
import type Store from "@ember-data/store";
import { next } from "@ember/runloop";
import { service } from "@ember/service";

import type { ValidatorMap } from "ember-changeset/types";
import type { IntlService } from "ember-intl";
import type { ForcBaseArgs } from "parog-web/components/forc/abstract/base-component";
import CrudJournalEventBasedComponent from "parog-web/components/forc/abstract/crud-journal-event-based-component";
import JournalEventRetrievalStrategies from "parog-web/components/forc/utils/journal-event-retrieval-strategies";
import ConsultationPlannedJournalEvent from "parog-web/models/consultation-planned-journal-event";
import type Creator from "parog-web/models/creator";
import type MedicModel from "parog-web/models/medic";
import type Patient from "parog-web/models/patient";
import type ConfigurationService from "parog-web/services/configuration";
import { Configuration } from "parog-web/services/configuration";
import type SemeiaSessionService from "parog-web/services/session";
import type { ConsultationType } from "../utils/compute-consultation-type-id";
import computeConsultationTypeId from "../utils/compute-consultation-type-id";

interface Args extends ForcBaseArgs {
  patient: Patient;
  hideTitle: boolean;
}

interface ComputedConsultationType {
  key: string;
  label: string;
}

type ConsultationEditionFormModel = ConsultationType & {
  consultationTypeId?: string;
  responsible: Creator;
};

export default class ConsultationEditionFormComponent extends CrudJournalEventBasedComponent<Args> {
  static fullComponentId = "consultation/edition-form";

  @service declare store: Store;
  @service declare session: SemeiaSessionService;
  @service declare configuration: ConfigurationService;
  @service declare intl: IntlService;

  get hideTitle() {
    return (this.args as Args & { hideTitle: boolean }).hideTitle;
  }

  static forcDisplayName = "Rendez-vous";

  get validations(): ValidatorMap {
    return this.journalEventClass.validations(this.intl);
  }

  static journalEventClass = ConsultationPlannedJournalEvent;

  retrieveJournalEvent = JournalEventRetrievalStrategies.sourceJournalEvent;

  get forcDisplayName(): string {
    return this.hideTitle ? "" : super.forcDisplayName;
  }

  /**
   * This represents a type of consultation.
   * It regroups a procedure and a provider.
   */
  get consultationTypes(): Map<string, ConsultationType> {
    const map = new Map();

    const consultationAllowedTypes = (
      this.configuration.configurationForPatient(
        Configuration.Consultation,
        this.args.patient
      ).consultationAllowedTypes || []
    ).filter(consultationType => consultationType.enabled);

    consultationAllowedTypes.forEach(consultationType =>
      map.set(
        computeConsultationTypeId(consultationType),
        this._verifyConsultationType(consultationType)
      )
    );

    return map;
  }

  /**
   * Get all the ConsultationType defined in the configuration files
   * and transform them into ComputedConsultationType by generating ids.
   */
  get computedConsultationTypes(): Array<ComputedConsultationType> {
    return Array.from(this.consultationTypes).map(([id, consultationType]) => ({
      key: id,
      label: consultationType.procedure.sourceLabel
    }));
  }

  get model(): ConsultationEditionFormModel {
    next(this, this.initChangeset);
    // eslint-disable-next-line parog-web/no-use-of-model-in-crud-forc
    return super.model;
  }

  initChangeset() {
    if (this.isNewMode) {
      if (this.computedConsultationTypes.length == 1) {
        this.changeset.consultationTypeId =
          this.computedConsultationTypes[0].key;
      }
    } else {
      this.changeset.consultationTypeId = computeConsultationTypeId(
        this.changeset as unknown as ConsultationType
      );
    }

    if (this.isNewMode && !this.changeset.responsible) {
      this.changeset.responsible = this.session.medic as Creator;
    }

    return this.changeset;
  }

  beforeValidation() {
    const consultationType = this.consultationTypes.get(
      this.changeset.consultationTypeId as string
    );

    if (!consultationType) {
      throw new Error(
        `Impossible to save the 'consultation-planned-journal-event' because there is no consultation type corresponding to the id '${this.changeset.consultationTypeId}'.`
      );
    }

    this.changeset.set("procedure", consultationType.procedure);
    this.changeset.set("provider", consultationType.provider);
  }

  async save(): Promise<ConsultationPlannedJournalEvent> {
    const je = await super.save();
    if (
      this.args.patient.therapeuticalArea?.id == "onco" &&
      this.args.patient.healthCentre?.isgt
    ) {
      await this.store.query("treatment-history-changed-journal-event", {
        filter: { patient_id: this.args.patient.id }
      });
    }

    if (je.treatmentHistoryChangedJournalEvent) {
      await je.treatmentHistoryChangedJournalEvent.clearAndFetchAssociatedTaskAndConsultation();
    }

    return je;
  }

  /**
   * This method set the missing values if there is,
   * like the source-concept for the procedure and the provider for example.
   * And throw errors if there is missing values.
   */
  _verifyConsultationType(
    consultationType: ConsultationType
  ): ConsultationType {
    const provider = consultationType.provider;
    provider.specialtySourceConcept = provider.specialtySourceConcept || "SPE";

    if (!provider.specialtySourceValue) {
      throw new Error(
        `The following provider is missing a 'specialtySourceValue': ${provider}`
      );
    }

    if (!provider.specialtySourceLabel) {
      throw new Error(
        `The following provider is missing a 'specialtySourceLabel': ${provider}`
      );
    }

    const procedure = consultationType.procedure;
    procedure.sourceConcept = procedure.sourceConcept || "SEMEIA";

    if (!procedure.sourceValue) {
      throw new Error(
        `The following procedure is missing a 'sourceValue': ${procedure}`
      );
    }

    if (!procedure.sourceLabel) {
      throw new Error(
        `The following procedure is missing a 'sourceLabel': ${procedure}`
      );
    }

    return {
      procedure,
      provider
    };
  }

  onChange(responsible: MedicModel | undefined) {
    this.changeset.set("responsible", responsible);
  }

  get responsible(): MedicModel | undefined {
    return (
      this.changeset.get("responsible")?.content ||
      this.changeset.get("responsible")
    );
  }
}
